import { useCallback, useEffect, useState } from "react";
import api from "../api/client";
import { Award, Loader2, RefreshCw } from "lucide-react";
import logger from "../lib/logger";

function scoreColor(score) {
  if (score == null) return { bg: "#F3F4F6", fg: "#6B7280", border: "#E5E7EB" };
  if (score >= 85) return { bg: "#16a34a", fg: "#FFFFFF", border: "#15803d" };
  if (score >= 65) return { bg: "#65a30d", fg: "#FFFFFF", border: "#4d7c0f" };
  if (score >= 40) return { bg: "#f59e0b", fg: "#1f2937", border: "#d97706" };
  return { bg: "#ef4444", fg: "#FFFFFF", border: "#b91c1c" };
}

/**
 * Compact personal SLA card shown on the Team Member dashboard.
 * Pulls the same _compute_worker_sla aggregation but scoped to the caller.
 */
export default function MySLACard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/reports/me/sla", { params: { weeks: 4 } });
      setData(data);
    } catch (err) {
      logger.error("My SLA fetch failed:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const me = data?.me;
  const c = scoreColor(me?.compliance_score);

  return (
    <div className="border border-gray-300 bg-white p-5" data-testid="my-sla-card">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-gray-500 flex items-center gap-1">
            <Award className="w-3 h-3" /> My compliance · last 4 weeks
          </div>
          <h3 className="font-black tracking-tighter text-xl mt-0.5">
            How am I tracking?
          </h3>
        </div>
        <button
          onClick={load}
          disabled={loading}
          data-testid="my-sla-refresh"
          className="px-2 py-1 border border-gray-300 text-xs flex items-center gap-1 hover:bg-gray-100 disabled:opacity-50"
        >
          {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
        </button>
      </div>

      {!data && loading && (
        <div className="py-6 text-center text-sm text-gray-500 flex items-center justify-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      )}

      {me && (
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="col-span-2 sm:col-span-1 flex flex-col items-start">
            <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">Score</div>
            <div
              data-testid="my-sla-score"
              className="text-3xl font-black tracking-tighter px-3 py-2 border min-w-[80px] text-center"
              style={{ backgroundColor: c.bg, color: c.fg, borderColor: c.border }}
            >
              {me.compliance_score == null ? "—" : me.compliance_score}
            </div>
          </div>
          <Tile label="Sign-ins" value={me.sign_ins} testid="my-sla-signins" />
          <Tile label="Hours" value={me.hours} testid="my-sla-hours" />
          <Tile label="Completions" value={me.completions} testid="my-sla-completions" />
          <Tile
            label="Photos attached"
            value={`${me.photos} (${Math.round(me.photo_rate * 100)}%)`}
            testid="my-sla-photos"
          />
          <Tile label="Sites worked" value={me.sites_worked} testid="my-sla-sites" />
          <Tile
            label="Left geofence"
            value={me.left_geofence}
            warning={me.left_without_return > 0}
            testid="my-sla-leftgeo"
          />
          <Tile
            label="Overrides"
            value={`${me.overrides_approved} ok / ${me.overrides_denied} ✕`}
            testid="my-sla-overrides"
          />
        </div>
      )}

      {data && !me && (
        <div className="py-4 text-sm text-gray-500" data-testid="my-sla-empty">
          No activity yet — once you clock in and complete tasks, your score will appear here.
        </div>
      )}
    </div>
  );
}

function Tile({ label, value, warning, testid }) {
  return (
    <div data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-gray-500">{label}</div>
      <div className={`text-lg font-bold mt-0.5 ${warning ? "text-red-700" : "text-gray-900"}`}>
        {value}
      </div>
    </div>
  );
}
